import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import axios from "../api/axios";
import { PreferencesContext } from "../context/PreferencesContext";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Autocomplete,
  Stack,
  Divider,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import PartialDeliveryDialog from "../components/PartialDeliveryDialog";

// import ReactToPrint from "react-to-print";
import { useReactToPrint } from "react-to-print";
import SaleReceipt from "../components/SaleReceipt";

import CommonTable from "../components/CommonTable";
import { useMessage } from "../context/MessageContext";
import { useConfirm } from "../context/ConfirmContext";
import { AuthContext } from "../context/AuthContext";
import { formatSaleDisplayId } from "../utils/saleHelpers";

const Sales = () => {
  const { organization, academicYear } = useContext(PreferencesContext);
  const [branch, setBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [sales, setSales] = useState([]);
  //   const [catalog, setCatalog] = useState(null);
  const [open, setOpen] = useState(false);

  const defaultForm = {
    studentId: "",
    studentName: "",
    grade: "",
    program: "",
    items: [],
    status: "Created",
    totalAmount: "",
    paymentMode: "",
    paymentInfo: "",
    manualReceiptNumber: "",
    discountVouchers: [],
    saleDate: new Date(), // default to now
    roundOff: 0,
  };
  const [form, setForm] = useState(defaultForm);

  const [itemsOptions, setItemOptions] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  const [deliverDialogOpen, setDeliverDialogOpen] = useState(false);
  const [deliverSale, setDeliverSale] = useState(null);

  const [studentInput, setStudentInput] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentOptions, setStudentOptions] = useState([]);

  const [step, setStep] = useState(1);

  const paymentModes = ["Cash", "UPI", "Bank Transfer", "Card"];
  const [splitPayment, setSplitPayment] = useState(false);
  const [paymentSplits, setPaymentSplits] = useState([
    { mode: "", amount: "", info: "" },
  ]);

  const [studentCatalog, setStudentCatalog] = useState({
    items: [],
    collections: [],
  });

  const [sourceInventory, setSourceInventory] = useState([]);

  const receiptRef = useRef();
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptSale, setReceiptSale] = useState(null);

  const { showMessage } = useMessage();
  const { confirm } = useConfirm();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    if (studentInput.length < 2) return;
    const fetchStudents = async () => {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `/students?orgId=${organization}&academicYear=${academicYear}&branch=${branch}&q=${studentInput}`,
        { headers: { "x-auth-token": token } }
      );
      setStudentOptions(res.data);
    };
    fetchStudents();
  }, [studentInput, organization, academicYear, branch]);

  useEffect(() => {
    setStudentOptions([]);
  }, [organization, academicYear, branch]);

  useEffect(() => {
    const fetchInventory = async () => {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `/inventory/balance?organization=${organization}&academicYear=${academicYear}&branch=${branch}`,
        { headers: { "x-auth-token": token } }
      );
      setSourceInventory(res.data || []);
    };
    if (organization && branch) fetchInventory();
  }, [organization, branch]);

  const stockMap = useMemo(() => {
    const map = {};
    sourceInventory.forEach((inv) => {
      let key = null;
      if (inv.itemModel === "Collection") {
        key = `COL_${inv.item}`;
      } else {
        key = `${inv.item}`;
      }
      map[key] = inv.saleableQuantity ?? inv.quantity ?? 0;
    });
    return map;
  }, [sourceInventory]);

  useEffect(() => {
    if (selectedStudent) {
      setForm((f) => ({
        ...f,
        studentId: selectedStudent.studentId,
        studentName: selectedStudent.name,
        grade: selectedStudent.grade,
        program: selectedStudent.program,
        branch: selectedStudent.branch,
      }));
      fetchMapping(selectedStudent);
    }
  }, [selectedStudent]);

  // Fetch branches and catalog
  useEffect(() => {
    const fetchBranches = async () => {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/branches?orgId=${organization}`, {
        headers: { "x-auth-token": token },
      });
      setBranches(res.data);
    };
    if (organization) {
      fetchBranches();
    }
  }, [organization]);

  const fetchMapping = async (selectedStudent) => {
    const token = localStorage.getItem("token");
    const res = await axios.get(
      `/sale-item-mapping/find?organization=${organization}&academicYear=${academicYear}&branch=${selectedStudent?.branch}&grade=${selectedStudent?.grade}&program=${selectedStudent?.program}`,
      { headers: { "x-auth-token": token } }
    );

    setStudentCatalog({
      items: res.data?.items,
      collections: res.data?.collections,
    });
    // setAllowedItemIds(res.data?.itemIds || []);
    // setAllowedCollectionIds(res.data?.collectionIds || []);
  };
  // Fetch sales for branch
  const fetchSales = async () => {
    const token = localStorage.getItem("token");
    const res = await axios.get(
      `/sales?orgId=${organization}&academicYear=${academicYear}&branch=${branch}&saleType=books`,
      { headers: { "x-auth-token": token } }
    );
    setSales(res.data || []);
  };
  useEffect(() => {
    if (branch) fetchSales();
  }, [branch]);

  useEffect(() => {
    // reset if dialog closed
    if (!open) {
      setForm(defaultForm);
      setSelectedStudent(null);
      setSelectedItems([]);
      setStep(1);
      setSplitPayment(false);
      setPaymentSplits([{ mode: "", amount: "", info: "" }]);
    }
  }, [open]);

  useEffect(() => {
    // Item options (filter as needed)
    const itemsOptions = [
      ...(studentCatalog?.items || []).map((it) => ({
        label: it?.item?.name,
        id: it?.item?._id,
        isCollection: false,
        price: it?.item?.price,
        quantity: it?.quantity,
      })),
      ...(studentCatalog?.collections || []).map((col) => ({
        label: "[Set] " + col?.collection?.name,
        id: col?.collection?._id,
        isCollection: true,
        price: col?.collection?.price,
        quantity: col.quantity,
      })),
    ];
    setItemOptions(itemsOptions);
    setSelectedItems(itemsOptions);
  }, [studentCatalog]);

  // Add item to sale
  const [itemEntry, setItemEntry] = useState(null);
  const handleAddItem = () => {
    if (
      itemEntry &&
      !selectedItems.some(
        (it) =>
          it.id === itemEntry.id && it.isCollection === itemEntry.isCollection
      )
    ) {
      setSelectedItems([...selectedItems, { ...itemEntry, quantity: 1 }]);
      setItemEntry(null);
    }
  };

  const formatPaymentExport = (sale) => {
    if (sale.payments?.length > 0) {
      return sale.payments
        .map(
          (p) =>
            `${p.mode}: ₹${p.amount}${p.info ? ` (${p.info})` : ""}`
        )
        .join("; ");
    }
    return sale.paymentMode || "";
  };

  const getPaymentSplitsTotal = () =>
    paymentSplits.reduce(
      (sum, p) => sum + (parseFloat(p.amount) || 0),
      0
    );

  const buildReceiptPaymentHtml = (sale) => {
    if (sale.payments?.length > 1) {
      const rows = sale.payments
        .map(
          (p) => `
        <tr>
          <td>${p.mode}</td>
          <td align="right">₹${parseFloat(p.amount).toLocaleString()}</td>
          <td>${p.info || "-"}</td>
        </tr>
      `
        )
        .join("");
      return `
        <p><strong>Payment Breakdown</strong></p>
        <table>
          <thead>
            <tr>
              <th>Mode</th>
              <th align="right">Amount</th>
              <th>Reference</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      `;
    }

    const mode =
      sale.payments?.[0]?.mode || sale.paymentMode || "-";
    const info = sale.payments?.[0]?.info || sale.paymentInfo;
    return `<p>Payment Mode: ${mode}${
      info ? " | Ref: " + info : ""
    }</p>`;
  };

  // Save sale
  const handleSave = async () => {
    const token = localStorage.getItem("token");
    const totalAmount = getTotalAmount(selectedItems);
    if (!selectedStudent)
      return showMessage("Please select a student", "warning");
    if (selectedItems.length === 0)
      return showMessage("Please add at least one item", "warning");

    let paymentsPayload;
    if (splitPayment) {
      const validPayments = paymentSplits.filter(
        (p) => p.mode && parseFloat(p.amount) > 0
      );
      if (validPayments.length === 0) {
        return showMessage(
          "Please add at least one payment with mode and amount.",
          "warning"
        );
      }
      const paymentSum = validPayments.reduce(
        (sum, p) => sum + parseFloat(p.amount),
        0
      );
      if (Math.abs(paymentSum - totalAmount) > 0.01) {
        return showMessage(
          `Payment total (₹${paymentSum.toLocaleString()}) must equal sale total (₹${totalAmount.toLocaleString()}).`,
          "error"
        );
      }
      paymentsPayload = validPayments.map((p) => ({
        mode: p.mode,
        amount: parseFloat(p.amount),
        info: p.info || "",
      }));
    } else if (!form.paymentMode) {
      return showMessage("Please select a payment mode", "warning");
    }

    const hasInvalidVoucher = (form?.discountVouchers || []).some(
      (v) =>
        !v.code?.trim() || // missing or empty code
        isNaN(v.amount) || // not a number
        parseFloat(v.amount) <= 0 // zero or negative
    );

    if (hasInvalidVoucher) {
      return showMessage(
        "Please ensure all discount vouchers have both a valid code and amount > 0.",
        "error"
      );
    }

    const salePayload = {
      ...form,
      organization,
      academicYear,
      branch,
      saleType: "books",
      student: selectedStudent?._id,
      items: selectedItems.map((it) => {
        if (it.isCollection) {
          return {
            collection: it.id,
            isCollection: true,
            quantity: it.quantity,
            price: it.price,
          };
        } else {
          return {
            item: it.id,
            isCollection: false,
            quantity: it.quantity,
            price: it.price,
          };
        }
      }),
      totalAmount,
    };

    if (splitPayment) {
      salePayload.payments = paymentsPayload;
      delete salePayload.paymentMode;
      delete salePayload.paymentInfo;
    }

    await axios.post(`/sales`, salePayload, {
      headers: { "x-auth-token": token },
    });
    setOpen(false);
    setSelectedItems([]);
    fetchSales();
  };

  //   // Mark delivered
  //   const handleDeliver = async (id) => {
  //     const token = localStorage.getItem("token");
  //     await axios.post(
  //       `/sales/${id}/deliver`,
  //       {},
  //       { headers: { "x-auth-token": token } }
  //     );
  //     fetchSales();
  //   };

  const handleDeliver = async (deliveryItems) => {
    const token = localStorage.getItem("token");
    await axios.post(
      `/sales/${deliverSale._id}/deliver`,
      { items: deliveryItems },
      { headers: { "x-auth-token": token } }
    );
    setDeliverDialogOpen(false);
    setDeliverSale(null);
    fetchSales(); // refresh sales list
  };

  const getTotalAmount = (selectedItems) => {
    const rawTotal = selectedItems.reduce(
      (sum, item) => sum + item.quantity * item.price,
      0
    );
    const totalDiscount = (form.discountVouchers || []).reduce(
      (sum, v) => sum + (parseFloat(v.amount) || 0),
      0
    );
    return rawTotal - totalDiscount + (parseFloat(form.roundOff) || 0);
  };

  const getReceiptNumber = (saleNumber) => formatSaleDisplayId(saleNumber);

  const handlePrintReceipt = (sale) => {
    setReceiptSale({ ...sale, receiptNo: getReceiptNumber(sale?.saleNumber) });
    setReceiptDialogOpen(true);
  };

  const handlePrint = useReactToPrint({
    content: () => receiptRef.current,
    removeAfterPrint: true,
  });

  const handlePrintInNewTab = () => {
    const printWindow = window.open("", "_blank");
    const doc = printWindow.document;

    doc.write(`
      <html>
        <head>
          <title>Receipt_${receiptSale?.receiptNo || receiptSale?._id}</title>
          <style>
            body { font-family: sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; }
            th { background: #f4f4f4; }
          </style>
        </head>
        <body>
          ${generateReceiptHtml(receiptSale, organization, branches)}
        </body>
      </html>
    `);
    doc.close();
    printWindow.focus();
    printWindow.print();
  };

  const generateReceiptHtml = (sale, orgName, branches) => {
    const branchName = branches.find((b) => b._id === sale.branch)?.name || "";
    const itemsHtml = (sale.items || [])
      .map(
        (it) => `
      <tr>
        <td>${
          it.isCollection
            ? `[Set] ${it.collection?.name || ""}`
            : it.item?.name || ""
        }</td>
        <td align="right">${it.quantity}</td>
        <td align="right">₹${(it.quantity * it.price).toLocaleString()}</td>
      </tr>
    `
      )
      .join("");

    return `
      <h2 style="text-align:center;">${"JASS Educational Services Pvt. Ltd."}</h2>
      <p>Receipt No: ${sale.receiptNo || sale._id}</p>
      ${
        sale.manualReceiptNumber
          ? `<p>Manual Receipt No: <strong>${sale.manualReceiptNumber}</strong></p>`
          : ""
      }
      <p>Date: ${
        sale?.saleDate ? new Date(sale.saleDate).toLocaleString() : ""
      }</p>
      <p>Student: ${sale.studentName} (${sale.studentId})</p>
      <p>Grade: ${sale.grade} | Program: ${sale.program}</p>
      <p>Branch: ${branchName}</p>
      <table>
        <thead>
          <tr>
            <th>Item/Collection (Books)</th>
            <th>Qty</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          ${itemsHtml}
          ${
            (sale.discountVouchers?.length || 0) > 0
              ? `
                ${sale.discountVouchers
                  .map(
                    (v) => `
                  <tr>
                    <td colspan="2" align="right">Discount (${
                      v.code || "Voucher"
                    })</td>
                    <td align="right">– ₹${parseFloat(
                      v.amount || 0
                    ).toLocaleString()}</td>
                  </tr>
                `
                  )
                  .join("")}
              `
              : ""
          }
          ${
            sale.roundOff
              ? `<tr>
                <td colspan="2" align="right">Round Off</td>
                <td align="right">₹${parseFloat(
                  sale.roundOff
                ).toLocaleString()}</td>
              </tr>`
              : ""
          }
          
          <tr>
            <td colspan="2" align="right"><strong>Grand Total</strong></td>
            <td align="right"><strong>₹${sale.totalAmount?.toLocaleString()}</strong></td>
          </tr>
        </tbody>
      </table>
      ${buildReceiptPaymentHtml(sale)}
      <p>Thank you!</p>
      <p style="text-align:right;"><small>Powered by JASS ERP System</small></p>
    `;
  };

  const openNewPurchase = () => {
    if (!branch) {
      return showMessage("Please select a branch", "warning");
    }
    setOpen(true);
  };

  const getVisibleBranches = () => {
    return user.role === "branch_manager"
      ? branches.filter((b) => user.branches.includes(b._id))
      : branches;
  };
  const getBranchName = (branchId) => {
    return branches.find((b) => b._id === branchId)?.name || "";
  };

  useEffect(() => {
    if (branches?.length > 0) {
      const visibleBranches = getVisibleBranches();

      if (visibleBranches?.length === 1) {
        setBranch(visibleBranches[0]._id);
      }
    }
  }, [branches, user]);

  const existingSaleForStudent = useMemo(() => {
    if (!selectedStudent) return null;
    return sales.find((s) => s.studentId === selectedStudent.studentId);
  }, [selectedStudent, sales]);

  const handleCancelSale = async (sale) => {
    const { confirmed, reason } = await confirm({
      title: "Cancel Purchase",
      message: `Are you sure you want to cancel sale ${formatSaleDisplayId(
        sale.saleNumber
      )}? This will also move back any items marked as delivered back into stock`,
      requireReason: true,
    });
    if (!confirmed) return;

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `/sales/${sale._id}/revert`,
        { reason },
        {
          headers: { "x-auth-token": token },
        }
      );
      showMessage("Sale cancelled successfully", "success");
      fetchSales(); // refresh
    } catch (err) {
      console.error("Cancel sale failed:", err);
      showMessage(err?.response?.data?.msg || "Failed to cancel sale", "error");
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Student Purchases (Books)
      </Typography>
      <FormControl sx={{ my: 2, minWidth: 250 }}>
        <InputLabel>Branch</InputLabel>
        <Select
          value={branch}
          onChange={(e) => setBranch(e.target.value)}
          label="Branch"
        >
          {getVisibleBranches()?.map((b) => (
            <MenuItem key={b._id} value={b._id}>
              {b.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="contained"
        onClick={openNewPurchase}
        sx={{ my: 2, ml: 2 }}
      >
        New Book Purchase
      </Button>
      <CommonTable
        downloadFileName="Purchases_Report"
        loading={false}
        rows={sales}
        columns={[
          {
            label: "Sale ID",
            renderCell: (s) => {
              const systemId = formatSaleDisplayId(s?.saleNumber);
              const manualId = s?.manualReceiptNumber
                ? ` | Manual: ${s.manualReceiptNumber}`
                : "";
              return `${systemId}${manualId}`;
            },
            getSearchValue: (s) => {
              const values = [];
              if (s?.saleNumber) values.push(formatSaleDisplayId(s.saleNumber));
              if (s?.manualReceiptNumber) values.push(s.manualReceiptNumber);
              return values.join(" ");
            },
          },
          {
            label: "System Sale ID",
            getExportValue: (s) => formatSaleDisplayId(s?.saleNumber),
            exportOnly: true, // custom flag to hide in UI
          },
          {
            label: "Manual Receipt No",
            getExportValue: (s) => s?.manualReceiptNumber || "",
            exportOnly: true,
          },
          {
            label: "Payment Mode",
            getExportValue: (s) => formatPaymentExport(s),
            exportOnly: true,
          },
          {
            label: "Payment Info",
            getExportValue: (s) => s?.paymentInfo || "",
            exportOnly: true,
          },
          {
            label: "Discount Voucher Codes",
            getExportValue: (s) =>
              (s.discountVouchers || [])
                .map((v) => v.code)
                .filter(Boolean)
                .join(", "),
            exportOnly: true,
          },
          {
            label: "Sale Date",
            field: "saleDate",
            renderCell: (s) =>
              s.saleDate
                ? new Date(s.saleDate).toLocaleString("en-IN", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })
                : "-",
          },
          {
            label: "Student",
            renderCell: (s) =>
              s.studentName +
                "\n(" +
                s.grade +
                ", " +
                s.program +
                ", " +
                s.studentId +
                ")" || s.studentId,
            getSearchValue: (s) =>
              s.studentName +
                " (" +
                s.grade +
                " | " +
                s.program +
                " | " +
                s.studentId +
                ")" || s.studentId,
          },
          {
            label: "Student Name",
            getExportValue: (s) => s?.studentName || "",
            exportOnly: true,
          },
          {
            label: "Student ID",
            getExportValue: (s) => s?.studentId || "",
            exportOnly: true,
          },
          {
            label: "Grade",
            getExportValue: (s) => s?.grade || "",
            exportOnly: true,
          },
          {
            label: "Program",
            getExportValue: (s) => s?.program || "",
            exportOnly: true,
          },
          {
            label: "Branch",
            getExportValue: (s) => getBranchName(s?.branch) || "",
            exportOnly: true,
          },
          // { label: "Grade", field: "grade" },
          // { label: "Program", field: "program" },
          {
            label: "Items",
            flex: true,
            renderCell: (sale) => (
              <Box sx={{ whiteSpace: "pre-line" }}>
                {sale.items
                  .map((it) =>
                    it?.isCollection
                      ? `[Set] ${it?.collection?.name || ""} (${it.quantity})`
                      : `${it?.item?.name || ""} (${it.quantity})`
                  )
                  .join("\n")}
              </Box>
            ),
            getExportValue: (sale) =>
              sale.items
                .map((it) =>
                  it?.isCollection
                    ? `[Set] ${it?.collection?.name || ""} (${it.quantity})`
                    : `${it?.item?.name || ""} (${it.quantity})`
                )
                .join(", "),
          },
          {
            label: "Total",
            field: "totalAmount",
            renderCell: (s) => `₹${s.totalAmount?.toLocaleString()}`,
          },
          {
            label: "Discount Voucher Total (₹)",
            getExportValue: (s) =>
              (s.discountVouchers || []).reduce(
                (sum, v) => sum + (v.amount || 0),
                0
              ),
            exportOnly: true,
          },
          {
            label: "Status",
            renderCell: (sale) => (
              <Chip
                onClick={() => {}}
                label={
                  sale.deliveryStatus === "Created"
                    ? "To Deliver"
                    : sale.deliveryStatus
                }
                color={
                  sale.deliveryStatus === "Delivered"
                    ? "success"
                    : sale.deliveryStatus === "Partially Delivered"
                    ? "warning"
                    : sale.deliveryStatus === "Cancelled"
                    ? "error"
                    : "default"
                }
              />
            ),
            getExportValue: (sale) =>
              sale.deliveryStatus === "Created"
                ? "To Deliver"
                : sale.deliveryStatus,
          },
          {
            label: "Cancellation Reason",
            getExportValue: (s) => s?.cancellationReason || "",
            exportOnly: true,
          },
          {
            label: "Action",
            renderCell: (sale) => (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {sale.deliveryStatus !== "Cancelled" && (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handlePrintReceipt(sale)}
                    >
                      Print Receipt
                    </Button>

                    {sale.deliveryStatus !== "Delivered" && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          setDeliverSale(sale);
                          setDeliverDialogOpen(true);
                        }}
                      >
                        Update Deliverables
                      </Button>
                    )}
                  </>
                )}

                {/* Cancel Sale for admin/super_admin and not already cancelled */}
                {(user.role === "admin" || user.role === "super_admin") &&
                  sale.deliveryStatus !== "Cancelled" && (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => handleCancelSale(sale)}
                    >
                      Cancel
                    </Button>
                  )}
              </Box>
            ),
          },
        ]}
      />

      {/* Sale Dialog */}
      <Dialog
        open={open}
        onClose={() => {
          setStep(1);
          setOpen(false);
        }}
        fullWidth
      >
        <DialogTitle>Add Sale</DialogTitle>
        <DialogContent>
          {step === 1 && (
            <>
              {/* --- Student Autocomplete --- */}
              <Autocomplete
                sx={{ mt: 1 }}
                options={studentOptions}
                getOptionLabel={(s) => `${s.name} (${s.studentId})`}
                value={selectedStudent}
                onChange={(e, v) => {
                  setSelectedStudent(v);
                  if (v) {
                    setForm((f) => ({
                      ...f,
                      studentId: v.studentId,
                      studentName: v.name,
                      grade: v.grade,
                      program: v.program,
                    }));
                  } else {
                    setForm((f) => ({
                      ...f,
                      studentId: "",
                      studentName: "",
                      grade: "",
                      program: "",
                    }));
                  }
                }}
                inputValue={studentInput}
                onInputChange={(e, v) => setStudentInput(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Student"
                    helperText="Start typing to search student name or ID"
                  />
                )}
              />

              {/* --- Display student details after selection (read-only) --- */}
              {selectedStudent && (
                <Stack
                  direction="row"
                  spacing={2}
                  divider={<span style={{ color: "#ccc" }}>|</span>}
                  sx={{
                    my: 2,
                    p: 2,
                    background: "#f7f7f9",
                    borderRadius: 1,
                    alignItems: "center",
                  }}
                >
                  <Typography variant="subtitle1">
                    <strong>ID:</strong> {selectedStudent.studentId}
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>Name:</strong> {selectedStudent.name}{" "}
                    {selectedStudent.lastname}
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>Grade:</strong> {selectedStudent.grade}
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>Program:</strong> {selectedStudent.program}
                  </Typography>
                </Stack>
              )}

              {existingSaleForStudent && (
                <Box
                  sx={{
                    background: "#fff3cd",
                    color: "#856404",
                    p: 2,
                    my: 2,
                    border: "1px solid #ffeeba",
                    borderRadius: 1,
                  }}
                >
                  ⚠️ This student already has a sale entry.
                  <br />
                  Sale ID:{" "}
                  {formatSaleDisplayId(existingSaleForStudent.saleNumber)}
                  <br />
                  Status: {existingSaleForStudent.deliveryStatus}
                </Box>
              )}

              {/* --- Add item/collection with quantity (reuse as before) --- */}
              <Box sx={{ display: "flex", alignItems: "center", my: 2, mt: 4 }}>
                <FormControl sx={{ flex: 1, mr: 2 }}>
                  <InputLabel>Add Item/Collection (Books)</InputLabel>
                  <Select
                    value={itemEntry?.id || ""}
                    onChange={(e) => {
                      const opt = itemsOptions.find(
                        (it) => it.id === e.target.value
                      );
                      setItemEntry(opt || null);
                    }}
                    label="Add Item/Collection (Books)"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {itemsOptions.map((it) => (
                      <MenuItem key={it.id} value={it.id}>
                        {it.label} (₹{it.price})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Quantity"
                  type="number"
                  size="small"
                  sx={{ width: 120, mr: 2 }}
                  value={itemEntry?.quantity || 1}
                  onChange={(e) => {
                    let quantity = Math.max(1, parseInt(e.target.value) || 1);
                    setItemEntry((prev) =>
                      prev ? { ...prev, quantity } : null
                    );
                  }}
                  disabled={!itemEntry}
                  inputProps={{ min: 1 }}
                />
                <Button onClick={handleAddItem} disabled={!itemEntry}>
                  Add
                </Button>
              </Box>

              {/* --- Selected Items List with Quantity --- */}
              {selectedItems.map((item, idx) => (
                <Box
                  key={item.id}
                  sx={{ display: "flex", alignItems: "center", mb: 1 }}
                >
                  <Chip label={item.label} sx={{ mr: 2 }} />
                  <TextField
                    label="Quantity"
                    type="number"
                    size="small"
                    sx={{ width: 120, mr: 2 }}
                    value={item.quantity}
                    onChange={(e) => {
                      let quantity = Math.max(
                        1,
                        parseInt(e.target.value, 10) || 1
                      );
                      setSelectedItems((list) =>
                        list.map((it, i) =>
                          i === idx ? { ...it, quantity } : it
                        )
                      );
                    }}
                    inputProps={{ min: 1 }}
                  />
                  <Button
                    color="error"
                    onClick={() =>
                      setSelectedItems((list) =>
                        list.filter((_, i) => i !== idx)
                      )
                    }
                  >
                    Remove
                  </Button>
                </Box>
              ))}
            </>
          )}
          {step === 2 && (
            <>
              {/* --- Billing Summary Table --- */}
              {selectedItems.length > 0 && (
                <>
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Billing Summary
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Item/Collection (Books)</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Unit Price</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="right">
                            In Stock (
                            {branches.find((b) => b._id === branch)?.name})
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedItems.map((item, idx) => {
                          const stockKey = item?.isCollection
                            ? `COL_${item.id}`
                            : `${item.id}`;
                          const stock = stockMap[stockKey] ?? 0;
                          const enough = stock >= item.quantity;

                          return (
                            <TableRow key={item.id}>
                              <TableCell>{item.label}</TableCell>
                              <TableCell align="right">
                                {item.quantity}
                              </TableCell>
                              <TableCell align="right">₹{item.price}</TableCell>
                              <TableCell align="right">
                                ₹{(item.quantity * item.price).toLocaleString()}
                              </TableCell>
                              <TableCell align="right">
                                <span
                                  style={{
                                    color: enough ? "#388e3c" : "#d32f2f",
                                    fontWeight: enough ? "normal" : "bold",
                                  }}
                                >
                                  {stock} {enough ? "✔️" : "❌"}
                                </span>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {(form?.discountVouchers || []).map((v, idx) => (
                          <TableRow key={`voucher-${idx}`}>
                            <TableCell colSpan={2} align="right">
                              Discount {v.code ? `(${v.code})` : ""}
                            </TableCell>
                            <TableCell align="right">
                              – ₹{parseFloat(v.amount || 0).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={3} align="right">
                            Round Off
                          </TableCell>
                          <TableCell align="right">
                            ₹{parseFloat(form.roundOff || 0).toLocaleString()}
                          </TableCell>
                        </TableRow>
                        {/* Grand total row */}
                        <TableRow>
                          <TableCell colSpan={3} align="right">
                            <strong>Grand Total</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>
                              ₹{getTotalAmount(selectedItems).toLocaleString()}
                            </strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>

                  <Box sx={{ my: 2 }}>
                    <Typography variant="subtitle1">
                      Discount Vouchers
                    </Typography>
                    {form.discountVouchers.map((v, idx) => (
                      <Stack
                        key={idx}
                        direction="row"
                        spacing={2}
                        sx={{ my: 1 }}
                      >
                        <TextField
                          label="Voucher Code"
                          value={v.code}
                          onChange={(e) => {
                            const updated = [...form.discountVouchers];
                            updated[idx].code = e.target.value;
                            setForm((f) => ({
                              ...f,
                              discountVouchers: updated,
                            }));
                          }}
                        />
                        <TextField
                          label="Discount ₹"
                          type="number"
                          value={v.amount}
                          onChange={(e) => {
                            const updated = [...form.discountVouchers];
                            updated[idx].amount =
                              parseFloat(e.target.value) || 0;
                            setForm((f) => ({
                              ...f,
                              discountVouchers: updated,
                            }));
                          }}
                        />
                        <Button
                          color="error"
                          onClick={() => {
                            const updated = [...form.discountVouchers];
                            updated.splice(idx, 1);
                            setForm((f) => ({
                              ...f,
                              discountVouchers: updated,
                            }));
                          }}
                        >
                          Remove
                        </Button>
                      </Stack>
                    ))}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          discountVouchers: [
                            ...(f.discountVouchers || []),
                            { code: "", amount: 0 },
                          ],
                        }))
                      }
                    >
                      + Add Voucher
                    </Button>
                  </Box>

                  <TextField
                    label="Round Off (₹)"
                    type="number"
                    fullWidth
                    inputProps={{ max: 150, min: -150 }}
                    value={form.roundOff}
                    onChange={(e) => {
                      let round = parseFloat(e.target.value);
                      if (isNaN(round)) round = 0;
                      if (round > 150) round = 150;
                      if (round < -150) round = -150;
                      setForm((f) => ({ ...f, roundOff: round }));
                    }}
                    sx={{ mt: 2 }}
                    margin="normal"
                  />

                  <TextField
                    type="datetime-local"
                    label="Purchase Date"
                    value={
                      form.saleDate
                        ? new Date(
                            new Date(form.saleDate).getTime() -
                              new Date().getTimezoneOffset() * 60000
                          )
                            .toISOString()
                            .slice(0, 16)
                        : ""
                    }
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        saleDate: new Date(e.target.value),
                      }))
                    }
                    fullWidth
                    margin="normal"
                    sx={{ mt: 2 }}
                  />

                  {/* --- Payment Mode --- */}
                  {!splitPayment ? (
                    <>
                      <FormControl fullWidth margin="normal" sx={{ mt: 2 }}>
                        <InputLabel>Payment Mode</InputLabel>
                        <Select
                          value={form.paymentMode}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              paymentMode: e.target.value,
                            }))
                          }
                          label="Payment Mode"
                        >
                          {paymentModes.map((mode) => (
                            <MenuItem key={mode} value={mode}>
                              {mode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        label="Payment Reference or Remarks"
                        value={form.paymentInfo}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            paymentInfo: e.target.value,
                          }))
                        }
                        fullWidth
                        margin="normal"
                      />
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          setSplitPayment(true);
                          setPaymentSplits([
                            {
                              mode: form.paymentMode || "",
                              amount: getTotalAmount(selectedItems),
                              info: form.paymentInfo || "",
                            },
                          ]);
                        }}
                        sx={{ mt: 0.5, px: 0 }}
                      >
                        Split across multiple payment modes?
                      </Button>
                    </>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Payment Split
                      </Typography>
                      {paymentSplits.map((p, idx) => (
                        <Stack
                          key={idx}
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          alignItems={{ sm: "center" }}
                          sx={{ mb: 1 }}
                        >
                          <FormControl sx={{ minWidth: 150 }}>
                            <InputLabel>Mode</InputLabel>
                            <Select
                              value={p.mode}
                              label="Mode"
                              onChange={(e) => {
                                const updated = [...paymentSplits];
                                updated[idx].mode = e.target.value;
                                setPaymentSplits(updated);
                              }}
                            >
                              {paymentModes.map((mode) => (
                                <MenuItem key={mode} value={mode}>
                                  {mode}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            label="Amount ₹"
                            type="number"
                            value={p.amount}
                            onChange={(e) => {
                              const updated = [...paymentSplits];
                              updated[idx].amount = e.target.value;
                              setPaymentSplits(updated);
                            }}
                            sx={{ width: 130 }}
                          />
                          <TextField
                            label="Ref (optional)"
                            value={p.info}
                            onChange={(e) => {
                              const updated = [...paymentSplits];
                              updated[idx].info = e.target.value;
                              setPaymentSplits(updated);
                            }}
                            sx={{ flex: 1 }}
                          />
                          {paymentSplits.length > 1 && (
                            <Button
                              color="error"
                              onClick={() => {
                                const updated = [...paymentSplits];
                                updated.splice(idx, 1);
                                setPaymentSplits(updated);
                              }}
                            >
                              Remove
                            </Button>
                          )}
                        </Stack>
                      ))}
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            setPaymentSplits([
                              ...paymentSplits,
                              { mode: "", amount: "", info: "" },
                            ])
                          }
                        >
                          + Add Payment
                        </Button>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => {
                            const first = paymentSplits[0];
                            setSplitPayment(false);
                            setForm((f) => ({
                              ...f,
                              paymentMode: first?.mode || f.paymentMode,
                              paymentInfo: first?.info || f.paymentInfo,
                            }));
                          }}
                        >
                          Use single payment mode
                        </Button>
                      </Stack>
                      <Typography
                        variant="body2"
                        color={
                          Math.abs(
                            getPaymentSplitsTotal() -
                              getTotalAmount(selectedItems)
                          ) <= 0.01
                            ? "text.secondary"
                            : "error"
                        }
                        sx={{ mt: 1 }}
                      >
                        Payment total: ₹
                        {getPaymentSplitsTotal().toLocaleString()} / Sale total:
                        ₹{getTotalAmount(selectedItems).toLocaleString()}
                        {Math.abs(
                          getPaymentSplitsTotal() -
                            getTotalAmount(selectedItems)
                        ) > 0.01 && " — amounts must match"}
                      </Typography>
                    </Box>
                  )}
                  <TextField
                    label="Manual Receipt Number"
                    value={form.manualReceiptNumber}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        manualReceiptNumber: e.target.value,
                      }))
                    }
                    fullWidth
                    margin="normal"
                  />
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {step === 2 ? (
            <>
              <Button onClick={() => setStep(1)}>Back</Button>
              <Button
                variant="contained"
                onClick={() => {
                  handleSave();
                }}
                disabled={selectedItems.length === 0}
              >
                Received Amount
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  setStep(1);
                  setOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => setStep(2)}
                disabled={!selectedStudent || selectedItems.length === 0}
              >
                Next
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <PartialDeliveryDialog
        open={deliverDialogOpen}
        onClose={() => setDeliverDialogOpen(false)}
        sale={deliverSale}
        stockMap={stockMap}
        onDeliver={handleDeliver}
      />

      {/* Receipt Dialog  */}
      <Dialog
        open={receiptDialogOpen}
        onClose={() => setReceiptDialogOpen(false)}
        fullWidth
      >
        <DialogTitle>Receipt</DialogTitle>
        <DialogContent>
          <SaleReceipt
            ref={receiptRef}
            sale={receiptSale}
            orgName={organization}
            branches={branches}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePrintInNewTab}
          >
            Print
          </Button>
          <Button onClick={() => setReceiptDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Sales;
